import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, User, Bookmark, Crown, CreditCard, Calendar,
    Loader2, Eye, Gift, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ImpersonateButton } from '../components/ImpersonationButtons';

const API = '/api';

// AdminUserDetail - dedicated page for /admin/users/:userId. Reached
// from the admin feedback table (each row's user email links here)
// and from every user-row on the admin users list. Deep-linkable so
// admins can share direct URLs to a user profile.
const AdminUserDetail = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchDetail = useCallback(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        fetch(`${API}/admin/users/${userId}`, { credentials: 'include' })
            .then(async (res) => {
                if (cancelled) return;
                if (res.status === 403) { setError('Admin access required'); return; }
                if (res.status === 404) { setError('User not found'); return; }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const body = await res.json();
                if (!cancelled) setData(body);
            })
            .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [userId]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="admin-user-loading">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen p-8 max-w-2xl mx-auto" data-testid="admin-user-error">
                <button onClick={() => navigate('/admin')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
                    <ArrowLeft className="w-4 h-4" /> Back to admin
                </button>
                <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800 text-sm">{error}</div>
            </div>
        );
    }

    const { user, stats, subscription, payment, recent_bookmarks } = data || {};

    return (
        <div className="min-h-screen bg-background" data-testid="admin-user-detail-page">
            <header className="border-b border-border bg-card">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="p-1.5 rounded hover:bg-secondary" aria-label="Back" data-testid="admin-user-back-btn">
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <User className="w-5 h-5 text-muted-foreground" />
                    <h1 className="text-lg font-semibold">User profile</h1>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
                {/* Identity card */}
                <section className="rounded-xl border border-border bg-card p-5 flex items-start gap-4">
                    {user?.picture ? (
                        <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
                            <User className="w-7 h-7 text-muted-foreground" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold tracking-tight truncate" data-testid="admin-user-name">{user?.name || '\u2014'}</h2>
                            <ImpersonateButton userId={userId} />
                            {subscription?.tier && subscription.tier !== 'free' && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                                    <Crown className="w-3 h-3 inline mr-1" />
                                    {subscription.tier}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-muted-foreground break-all" data-testid="admin-user-email">{user?.email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            User ID: <code className="bg-secondary px-1.5 py-0.5 rounded">{user?.user_id}</code>
                        </p>
                        {user?.created_at && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Joined {new Date(user.created_at).toLocaleDateString()} &middot; Last login {user.last_login ? new Date(user.last_login).toLocaleDateString() : '\u2014'}
                            </p>
                        )}
                    </div>
                </section>

                {/* Stats grid */}
                <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatBox icon={Bookmark} label="Bookmarks" value={stats?.bookmark_count ?? 0} testId="stat-bookmarks" />
                    <StatBox icon={Bookmark} label="Categories" value={stats?.category_count ?? 0} testId="stat-categories" />
                    <StatBox icon={Eye} label="Total visits" value={stats?.total_visits ?? 0} testId="stat-total-visits" />
                    <StatBox icon={Eye} label="Visits (30d)" value={stats?.visits_last_30d ?? 0} testId="stat-visits-30d" />
                    <StatBox icon={Crown} label="Tier" value={(subscription?.tier || 'free').replace('_', ' ')} testId="stat-tier" />
                    <StatBox icon={CreditCard} label="Plan" value={subscription?.plan || 'free'} testId="stat-plan" />
                </section>

                {/* Subscription card */}
                <section className="rounded-xl border border-border bg-card p-5">
                    <h3 className="text-sm font-semibold text-foreground mb-3">Subscription</h3>
                    <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <Field label="Tier" value={subscription?.tier || 'free'} />
                        <Field label="Status" value={subscription?.status || '\u2014'} />
                        <Field label="Bookmark limit" value={String(subscription?.bookmark_limit ?? '\u2014')} />
                        <Field label="Billing" value={subscription?.billing_interval || '\u2014'} />
                        {subscription?.current_period_end && (
                            <Field label="Access until" value={new Date(subscription.current_period_end).toLocaleDateString()} />
                        )}
                    </dl>
                </section>

                {/* Grant subscription card — admin-only free-tier upgrade */}
                <GrantSubscriptionCard
                    userId={user?.user_id}
                    currentTier={subscription?.tier || 'free'}
                    onGranted={fetchDetail}
                />

                {/* Recent bookmarks */}
                <section className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                        <Bookmark className="w-4 h-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">Recent bookmarks</h3>
                        <span className="text-xs text-muted-foreground ml-auto">
                            {recent_bookmarks?.length || 0} of {stats?.bookmark_count ?? 0}
                        </span>
                    </div>
                    {recent_bookmarks?.length > 0 ? (
                        <ul className="divide-y divide-border">
                            {recent_bookmarks.map((bm) => (
                                <li key={bm.bookmark_id} className="px-5 py-3 flex items-center gap-3" data-testid={`user-bookmark-${bm.bookmark_id}`}>
                                    <div className="flex-1 min-w-0">
                                        {bm.url ? (
                                            <a
                                                href={bm.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm font-medium truncate block hover:underline"
                                                data-testid={`user-bookmark-title-${bm.bookmark_id}`}
                                            >
                                                {bm.title || '(untitled)'}
                                            </a>
                                        ) : (
                                            <p className="text-sm font-medium truncate">{bm.title || '(untitled)'}</p>
                                        )}
                                        <p className="text-xs text-muted-foreground truncate">{bm.url}</p>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                                        {bm.content_type || 'link'}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="px-5 py-6 text-sm text-muted-foreground text-center">
                            This user hasn&#39;t saved any bookmarks yet.
                        </div>
                    )}
                </section>

                {/* Payment */}
                {payment && (
                    <section className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold text-foreground mb-3">Latest payment</h3>
                        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                            <Field label="Amount" value={payment.amount ? `$${payment.amount}` : '\u2014'} />
                            <Field label="Status" value={payment.status || '\u2014'} />
                            <Field label="When" value={payment.paid_at ? new Date(payment.paid_at).toLocaleString() : '\u2014'} />
                            <Field label="Method" value={payment.payment_method || '\u2014'} />
                        </dl>
                    </section>
                )}
            </main>
        </div>
    );
};

// ── Grant subscription card ─────────────────────────────────────────
// Lets an admin comp a user a Plus / Pro subscription for a specific
// window (or revoke it back to free). Historically the only knob was
// tier — no way to grant a 7-day trial or a 1-year founder comp
// without hitting the DB directly. Now the admin picks tier + duration
// and PATCHes /admin/users/:id/tier with both fields.
const DURATION_PRESETS = [
    { label: '7 days', days: 7 },
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
    { label: '1 year', days: 365 },
    { label: 'Lifetime', days: 0 },   // 0 == no end date
    { label: 'Custom...', days: null },
];

const TIER_LABELS = { pro_200: 'Plus (200/mo)', pro_unlimited: 'Pro (unlimited)' };

function GrantSubscriptionCard({ userId, currentTier, onGranted }) {
    // Start with nothing selected — the admin must pick tier + duration
    // deliberately before the Grant button unlocks. Prevents accidental
    // "gave them Plus for 30 days because I clicked the wrong button"
    // grants when the admin actually meant to just check the profile.
    const [tier, setTier] = useState(null);
    const [durationChoice, setDurationChoice] = useState(undefined);
    const [customDays, setCustomDays] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const isCustom = durationChoice === null;
    // Neither picked → Grant is disabled. `durationChoice === 0` is
    // Lifetime (valid), so we check for `undefined` explicitly.
    const canGrant = !!tier && durationChoice !== undefined && (!isCustom || customDays);

    const submit = async (targetTier, durationDays) => {
        if (!userId) return;
        setSubmitting(true);
        try {
            const body = { tier: targetTier };
            if (durationDays !== undefined) body.duration_days = durationDays;
            const res = await fetch(`${API}/admin/users/${userId}/tier`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.detail || `HTTP ${res.status}`);
            }
            const label = targetTier === 'free'
                ? 'Downgraded to free'
                : `Granted ${TIER_LABELS[targetTier]} for ${
                    durationDays === 0 ? 'lifetime' : `${durationDays} days`
                }`;
            toast.success(label);
            onGranted?.();
        } catch (e) {
            toast.error(e.message || 'Grant failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleGrant = () => {
        if (!tier) { toast.error('Pick a tier first'); return; }
        if (durationChoice === undefined) { toast.error('Pick a duration'); return; }
        let days = durationChoice;
        if (isCustom) {
            const parsed = parseInt(customDays, 10);
            if (!Number.isFinite(parsed) || parsed < 1 || parsed > 3650) {
                toast.error('Custom duration must be between 1 and 3650 days');
                return;
            }
            days = parsed;
        }
        submit(tier, days);
    };

    return (
        <section className="rounded-xl border border-border bg-card p-5" data-testid="grant-subscription-card">
            <div className="flex items-center gap-2 mb-3">
                <Gift className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-foreground">Grant free subscription</h3>
                {currentTier !== 'free' && (
                    <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                        Currently {currentTier.replace('_', ' ')}
                    </span>
                )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
                {/* Tier picker */}
                <div>
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Tier</label>
                    <div className="flex flex-wrap gap-2">
                        {['pro_200', 'pro_unlimited'].map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setTier(t)}
                                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                                    tier === t
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'bg-background text-muted-foreground border-border hover:bg-secondary'
                                }`}
                                data-testid={`grant-tier-${t}`}
                            >
                                {TIER_LABELS[t]}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Duration picker */}
                <div>
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Duration</label>
                    <div className="flex flex-wrap gap-2">
                        {DURATION_PRESETS.map((p) => (
                            <button
                                key={p.label}
                                type="button"
                                onClick={() => setDurationChoice(p.days)}
                                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                                    durationChoice === p.days
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'bg-background text-muted-foreground border-border hover:bg-secondary'
                                }`}
                                data-testid={`grant-duration-${p.days ?? 'custom'}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {isCustom && (
                        <div className="flex items-center gap-2 mt-2">
                            <Input
                                type="number"
                                min={1}
                                max={3650}
                                value={customDays}
                                onChange={(e) => setCustomDays(e.target.value)}
                                placeholder="Days"
                                className="h-8 text-xs w-28"
                                data-testid="grant-duration-custom-input"
                            />
                            <span className="text-xs text-muted-foreground">days</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-5 pt-4 border-t border-border/60">
                <Button
                    type="button"
                    onClick={handleGrant}
                    disabled={submitting || !canGrant}
                    size="sm"
                    className="gap-1.5"
                    data-testid="grant-submit-btn"
                >
                    <Sparkles className="w-3.5 h-3.5" />
                    {submitting ? 'Granting...' : 'Grant subscription'}
                </Button>
                {currentTier !== 'free' && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => submit('free')}
                        disabled={submitting}
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        data-testid="grant-revoke-btn"
                    >
                        Revoke &amp; downgrade to free
                    </Button>
                )}
            </div>
        </section>
    );
}

const StatBox = ({ icon: Icon, label, value, testId }) => (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
            <Icon className="w-3.5 h-3.5" />
            <span className="text-[11px] uppercase tracking-wider">{label}</span>
        </div>
        <p className="text-lg font-bold tracking-tight">{value}</p>
    </div>
);

const Field = ({ label, value }) => (
    <div>
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground mb-0.5">{label}</dt>
        <dd className="text-sm font-medium truncate">{value}</dd>
    </div>
);

export default AdminUserDetail;
